import { lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { SuspensedView } from '@components/SuspensedView'

import { MasterLayout } from '@/layouts/MasterLayout'
import { DashboardWrapper } from '@/pages/global/dashboard/DashboardPage'

const AppointmentTimeOffRoutes = lazy(() => import('./global/AppointmentTimeOffRoutes'))
const PriceTagRoutes = lazy(() => import('./global/PriceTagRoutes'))
const PharmacistRoutes = lazy(() => import('./global/PharmacistRoutes'))
const AppointmentSettingRoutes = lazy(() => import('./global/AppointmentSettingRoutes'))
const AppointmentCalendarRoutes = lazy(() => import('./global/AppointmentCalendarRoutes'))
const PharmaInventoryReportRoutes = lazy(() => import('./global/PharmaInventoryReportRoutes'))
const AccountingReportRoutes = lazy(() => import('@/routes/global/AccountingReportRoutes'))
const AccountRoutes = lazy(() => import('./global/AccountRoutes'))
const AppointmentTimeSlotRoutes = lazy(() => import('@/routes/global/AppointmentTimeSlotRoutes'))
const AppointmentTypeRoutes = lazy(() => import('./global/AppointmentTypeRoutes'))
const CashDepositRoutes = lazy(() => import('@/routes/global/CashDepositRoutes'))
const CashierRoutes = lazy(() => import('@/routes/global/CashierRoutes'))
const ClinicBookingClosedTimeRoutes = lazy(() => import('@/routes/global/ClinicBookingClosedTimeRoutes'))
const ClinicOttopayEdcRoutes = lazy(() => import('@/routes/global/ClinicOttopayEdcRoutes'))
const ClinicSettingRoutes = lazy(() => import('./global/ClinicSettingRoutes'))
const CompanyRoutes = lazy(() => import('./global/CompanyRoutes'))
const DailyReportPage = lazy(() => import('@/routes/global/DailyReportRoutes'))
const DoctorRoutes = lazy(() => import('@/routes/global/DoctorRoutes'))
const ExaminationRoutes = lazy(() => import('@/routes/global/ExaminationRoutes'))
const ManagementReportRoutes = lazy(() => import(`@/routes/global/ManagementReportRoutes`))
const MedicalRecordRoutes = lazy(() => import('@/routes/global/MedicalRecordRoutes'))
const MedicinalDispenseRoutes = lazy(() => import('@/routes/global/MedicinalDispenseRoutes'))
const NurseRoutes = lazy(() => import('@/routes/global/NurseRoutes'))
const ProductTransferClinicOutboundRoutes = lazy(() => import('@/routes/global/ProductTransferClinicOutboundRoutes'))
const PatientRoutes = lazy(() => import('@/routes/global/PatientRoutes'))
const PatientVisitRoutes = lazy(() => import('@/routes/global/PatientVisitRoutes'))
const PatientVitalCheckRoutes = lazy(() => import('@/routes/global/PatientVitalCheckRoutes'))
const PrinterGatewayRoutes = lazy(() => import('@/routes/global/PrinterGatewayRoutes'))
const ProductAdjustmentRoutes = lazy(() => import('@/routes/global/ProductAdjustmentRoutes'))
const ProductReceivesRoutes = lazy(() => import('@/routes/global/ProductReceiveRoutes'))
const ProductTransferRoutes = lazy(() => import('@/routes/global/ProductTransferRoutes'))
const ProductInventoryRoutes = lazy(() => import('@/routes/global/ProductInvetoryRoutes'))
const QueueDisplayRoutes = lazy(() => import('@/routes/global/QueueDisplayRoutes'))
const QueueRoutes = lazy(() => import('@/routes/global/QueueRoutes'))
const SummaryReportRoutes = lazy(() => import('@/routes/global/SummaryReportRoutes'))
const TaskRoutes = lazy(() => import('@/routes/global/TaskRoutes'))
const TreatmentRoutes = lazy(() => import('@/routes/global/TreatmentRoutes'))
const UserRoutes = lazy(() => import('@/routes/global/UserRoutes'))
const WaiveFeeReportRoutes = lazy(() => import('@/routes/global/WaiveFeeReportRoutes'))
const ICD10TrendingsPage = lazy(() => import('@/routes/global/ICD10TrendingRoutes'))
const ProductPromotionsRoutes = lazy(() => import('@/routes/global/ProductPromotionRoutes'))
const ProductBundlingRoutes = lazy(() => import('@/routes/global/ProductBundlingRoutes'))
const ProductTransferClinicInboundRoutes = lazy(() => import('@/routes/global/ProductTransferClinicInboundRoutes'))
const DiagnosticAncillaryRoutes = lazy(() => import('@/routes/global/DiagnosticAncillaryRoutes'))
const DiagnosticAncillaryActionRoutes = lazy(() => import('@/routes/global/DiagnosticAncillaryActionRoutes'))
const DiagnosticAncillarySummaryRoutes = lazy(() => import('@/routes/global/DiagnosticAncillarySummaryRoutes'))
const DiagnosticAncillaryTemplateRoutes = lazy(() => import('@/routes/global/DiagnosticAncillaryTemplateRoutes'))

const GlobalRoutes = () => {
  return (
    <Routes>
      {/* Redirect to Dashboard if no path */}
      <Route
        index
        element={<Navigate to="/dashboard" />}
      />

      <Route element={<MasterLayout />}>
        {/* Pages */}
        <Route
          path="dashboard"
          element={<DashboardWrapper />}
        />

        {/* Lazy Modules */}
        <Route
          path="companies/*"
          element={
            <SuspensedView>
              <CompanyRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="clinics/settings/*"
          element={
            <SuspensedView>
              <ClinicSettingRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="doctors/*"
          element={
            <SuspensedView>
              <DoctorRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="nurses/*"
          element={
            <SuspensedView>
              <NurseRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="users/*"
          element={
            <SuspensedView>
              <UserRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="queues/*"
          element={
            <SuspensedView>
              <QueueRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="queue-displays/*"
          element={
            <SuspensedView>
              <QueueDisplayRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="tasks/*"
          element={
            <SuspensedView>
              <TaskRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="appointment-time-offs/*"
          element={
            <SuspensedView>
              <AppointmentTimeOffRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="appointment-time-slots/*"
          element={
            <SuspensedView>
              <AppointmentTimeSlotRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="appointment-types/*"
          element={
            <SuspensedView>
              <AppointmentTypeRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="patient-visits/*"
          element={
            <SuspensedView>
              <PatientVisitRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="patient-vital-checks/*"
          element={
            <SuspensedView>
              <PatientVitalCheckRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="medical-record/*"
          element={
            <SuspensedView>
              <MedicalRecordRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="printer-gateways/*"
          element={
            <SuspensedView>
              <PrinterGatewayRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="icd-10-trendings/*"
          element={
            <SuspensedView>
              <ICD10TrendingsPage />
            </SuspensedView>
          }
        />

        <Route
          path="patients/*"
          element={
            <SuspensedView>
              <PatientRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="/account/*"
          element={
            <SuspensedView>
              <AccountRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="cashiers/*"
          element={
            <SuspensedView>
              <CashierRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="daily-report/*"
          element={
            <SuspensedView>
              <DailyReportPage />
            </SuspensedView>
          }
        />

        <Route
          path="summary-report/*"
          element={
            <SuspensedView>
              <SummaryReportRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="product-receives/*"
          element={
            <SuspensedView>
              <ProductReceivesRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="product-transfers/*"
          element={
            <SuspensedView>
              <ProductTransferRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="medicinal-dispenses/*"
          element={
            <SuspensedView>
              <MedicinalDispenseRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="clinic-ottopay-edcs/*"
          element={
            <SuspensedView>
              <ClinicOttopayEdcRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="pharmacists/*"
          element={
            <SuspensedView>
              <PharmacistRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="cash-deposit/*"
          element={
            <SuspensedView>
              <CashDepositRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="product-adjustment/*"
          element={
            <SuspensedView>
              <ProductAdjustmentRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="product-bundlings/*"
          element={
            <SuspensedView>
              <ProductBundlingRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="price-tags/*"
          element={
            <SuspensedView>
              <PriceTagRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="waive-fee-reports/*"
          element={
            <SuspensedView>
              <WaiveFeeReportRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="accounting-report/*"
          element={
            <SuspensedView>
              <AccountingReportRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="accounting-report/*"
          element={
            <SuspensedView>
              <AccountingReportRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="treatments/*"
          element={
            <SuspensedView>
              <TreatmentRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="examinations/*"
          element={
            <SuspensedView>
              <ExaminationRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="pharma-inventory-report/*"
          element={
            <SuspensedView>
              <PharmaInventoryReportRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="management-report/*"
          element={
            <SuspensedView>
              <ManagementReportRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="product-inventories/*"
          element={
            <SuspensedView>
              <ProductInventoryRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="clinic-booking-closed-times/*"
          element={
            <SuspensedView>
              <ClinicBookingClosedTimeRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="appointment-settings/*"
          element={
            <SuspensedView>
              <AppointmentSettingRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="appointment-calendar/*"
          element={
            <SuspensedView>
              <AppointmentCalendarRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="product-promotions/*"
          element={
            <SuspensedView>
              <ProductPromotionsRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="product-transfer-clinic-inbounds/*"
          element={
            <SuspensedView>
              <ProductTransferClinicInboundRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="product-transfer-clinic-outbounds/*"
          element={
            <SuspensedView>
              <ProductTransferClinicOutboundRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="diagnostic-ancillaries/*"
          element={
            <SuspensedView>
              <DiagnosticAncillaryRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="diagnostic-ancillary-actions/*"
          element={
            <SuspensedView>
              <DiagnosticAncillaryActionRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="diagnostic-ancillary-summaries/*"
          element={
            <SuspensedView>
              <DiagnosticAncillarySummaryRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="diagnostic-ancillary-templates/*"
          element={
            <SuspensedView>
              <DiagnosticAncillaryTemplateRoutes />
            </SuspensedView>
          }
        />

        {/* Redirect to Dashboard after success login/registartion */}
        <Route
          path="/auth/*"
          element={<Navigate to="/dashboard" />}
        />
      </Route>

      {/* Page Not Found */}
      <Route
        path="*"
        element={<Navigate to="/error/404" />}
      />
    </Routes>
  )
}

export { GlobalRoutes }
